﻿import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import InputAdornment from "@material-ui/core/InputAdornment";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import BuildOutlinedIcon from "@material-ui/icons/BuildOutlined";
import HttpOutlinedIcon from "@material-ui/icons/HttpOutlined";
import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const defaultActionForm = {
  id: null,
  name: "",
  description: "",
  method: "POST",
  endpoint: "",
  timeoutMs: 10000,
  headersJson: "",
  bodyTemplate: "",
  resultTemplate: "",
  responseMappingJson: "",
  isActive: true,
  requiresDocument: false,
  requiresNote: false,
  noteLabel: "",
  resultMode: "display",
  allowedUserIds: []
};

const defaultWabaForm = {
  wabaEnabled: false,
  wabaPhoneNumberId: "",
  wabaBusinessAccountId: "",
  wabaAccessToken: "",
  wabaVerifyToken: "",
  wabaWebhookUrl: ""
};

const defaultWabaTemplateForm = {
  name: "",
  language: "pt_BR",
  category: "UTILITY",
  bodyText: "",
  headerText: "",
  footerText: "",
  buttons: [],
  // Exemplo de cada variavel do corpo ({{1}}, {{2}}...): a Meta so aprova com
  // exemplo, e a Gupshup exige.
  bodyExamples: []
};

// Indices das variaveis do corpo, em ordem: "{{1}} ... {{2}}" -> [1, 2].
const templateVariableIndexes = text =>
  Array.from(
    new Set((String(text || "").match(/\{\{\s*(\d+)\s*\}\}/g) || []).map(token => Number(token.replace(/\D/g, ""))))
  ).sort((a, b) => a - b);

const defaultWabaCallingForm = {
  status: "ENABLED",
  callIconVisibility: "DEFAULT",
  callbackPermissionStatus: "ENABLED",
  sipEnabled: false,
  sipHostname: "",
  sipPort: "15060"
};

const buildDefaultTestForm = () => ({
  ticketId: "",
  contactId: "",
  variablesJson: "{}"
});

const RESPONSE_MAPPING_SOURCE_OPTIONS = [
  { value: "response.json", label: "Resposta JSON" },
  { value: "response.body", label: "Body bruto" },
  { value: "response.statusCode", label: "Status HTTP" },
  { value: "response.finalUrl", label: "URL final" }
];

const buildDefaultMappingDraft = () => ({
  key: "",
  source: "response.json",
  path: "",
  defaultValue: ""
});

const normalizeMappingSource = value => {
  const normalized = String(value || "response.json")
    .trim()
    .toLowerCase();

  if (RESPONSE_MAPPING_SOURCE_OPTIONS.some(item => item.value === normalized)) {
    return normalized;
  }

  return "response.json";
};

const sanitizeVariableKey = value => {
  let normalized = String(value || "")
    .trim()
    .replace(/[^\w]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (!normalized) {
    return "";
  }

  if (/^\d/.test(normalized)) {
    normalized = `var_${normalized}`;
  }

  return normalized;
};

const pathToVariableKey = value => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const normalized = raw.replace(/\[(\d+)\]/g, "_$1");
  const parts = normalized.split(".").filter(Boolean);
  const preferred = parts.length ? parts[parts.length - 1] : normalized;
  return sanitizeVariableKey(preferred);
};

const parseDefaultValueInput = value => {
  const text = String(value || "").trim();
  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
};

const parseResponseMappingJson = value => {
  const raw = String(value || "").trim();
  if (!raw) {
    return [];
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return [];
  }

  const usedKeys = new Set();
  const mappings = [];
  const pushMapping = mapping => {
    const key = sanitizeVariableKey(mapping?.key);
    if (!key || usedKeys.has(key)) {
      return;
    }

    const source = normalizeMappingSource(mapping?.source);
    const next = {
      key,
      source
    };

    if (source === "response.json") {
      next.path = String(mapping?.path || "").trim();
    }

    if (Object.prototype.hasOwnProperty.call(mapping || {}, "defaultValue")) {
      next.defaultValue = mapping.defaultValue;
    }

    usedKeys.add(key);
    mappings.push(next);
  };

  if (Array.isArray(parsed)) {
    parsed.forEach(item => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return;
      }
      pushMapping(item);
    });
    return mappings;
  }

  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    Object.entries(parsed).forEach(([key, valueEntry]) => {
      if (typeof valueEntry === "string") {
        pushMapping({
          key,
          source: "response.json",
          path: valueEntry
        });
        return;
      }

      if (!valueEntry || typeof valueEntry !== "object" || Array.isArray(valueEntry)) {
        return;
      }

      pushMapping({
        key,
        ...valueEntry
      });
    });
  }

  return mappings;
};

const stringifyResponseMappings = mappings => {
  if (!Array.isArray(mappings) || !mappings.length) {
    return "";
  }

  return JSON.stringify(mappings, null, 2);
};

const formatMappedVariablesTemplatePreview = mappings => {
  if (!Array.isArray(mappings) || !mappings.length) {
    return "";
  }

  return mappings
    .map(item => {
      const key = String(item?.key || "").trim();
      if (!key) {
        return "";
      }
      return `${key.toUpperCase()}: {{vars.${key}}}`;
    })
    .filter(Boolean)
    .join("\n");
};

const formatMappingPreviewValue = value => {
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const collectJsonPathOptions = (value, options = [], parentPath = "", depth = 0) => {
  if (depth > 6 || options.length >= 250) {
    return options;
  }

  if (Array.isArray(value)) {
    if (parentPath) {
      options.push({
        path: parentPath,
        preview: `[array:${value.length}]`
      });
    }

    value.slice(0, 8).forEach((item, index) => {
      const childPath = parentPath ? `${parentPath}[${index}]` : `[${index}]`;
      collectJsonPathOptions(item, options, childPath, depth + 1);
    });
    return options;
  }

  if (value && typeof value === "object") {
    if (parentPath) {
      options.push({
        path: parentPath,
        preview: "{objeto}"
      });
    }

    Object.entries(value)
      .slice(0, 40)
      .forEach(([key, nestedValue]) => {
        const childPath = parentPath ? `${parentPath}.${key}` : key;
        collectJsonPathOptions(nestedValue, options, childPath, depth + 1);
      });
    return options;
  }

  options.push({
    path: parentPath || "$",
    preview: formatMappingPreviewValue(value)
  });
  return options;
};

const normalizeIdList = value => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => Number(item))
    .filter(item => Number.isFinite(item) && item > 0);
};

const useStyles = makeStyles(theme => ({
  pagePaper: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    padding: theme.spacing(2.5),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(11,18,26,0.98) 0%, rgba(8,15,22,0.98) 100%)"
        : theme.palette.background.paper,
    borderColor:
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.12)"
        : "rgba(18, 140, 126, 0.10)",
    ...theme.scrollbarStyles
  },
  introCard: {
    padding: theme.spacing(2.25),
    borderRadius: 18,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.16)"
        : "1px solid rgba(18, 140, 126, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(19, 31, 43, 0.98) 0%, rgba(13, 24, 34, 0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(245,250,248,0.98) 100%)"
  },
  introTitle: {
    fontWeight: 800,
    marginBottom: theme.spacing(0.75)
  },
  introText: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6
  },
  tabsPaper: {
    width: "100%",
    flex: "0 0 auto",
    marginBottom: theme.spacing(1.5),
    borderRadius: 14,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.2)",
    overflow: "hidden",
    position: "relative",
    zIndex: 2,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(11,18,26,0.98) 0%, rgba(8,15,22,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,249,251,0.98) 100%)"
  },
  topTabsRow: {
    minHeight: 56,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.92)"
        : "rgba(241, 245, 249, 0.85)",
    borderRadius: 10,
    padding: theme.spacing(0.5),
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(0.75),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  topTabButton: {
    minHeight: 44,
    borderRadius: 8,
    border: "1px solid transparent",
    fontWeight: 800,
    fontSize: "0.95rem",
    lineHeight: 1.2,
    textTransform: "none",
    color: theme.palette.type === "dark" ? "#dbe7f3" : "#334155",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    backgroundColor: "transparent",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap"
  },
  topTabButtonActive: {
    color: theme.palette.type === "dark" ? "#59dbc8" : "#0f766e",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.24)"
        : "1px solid rgba(18, 140, 126, 0.24)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(45, 212, 191, 0.14)"
        : "rgba(18, 140, 126, 0.12)"
  },
  wabaRoadmap: {
    marginTop: theme.spacing(1.5),
    display: "grid",
    gap: theme.spacing(1)
  },
  wabaSubTabs: {
    marginTop: theme.spacing(1.5),
    borderBottom: "1px solid rgba(148, 163, 184, 0.28)",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(0.5)
  },
  wabaSubTabButton: {
    borderRadius: 999,
    minHeight: 42,
    textTransform: "none",
    fontWeight: 700,
    border: "1px solid rgba(148, 163, 184, 0.36)",
    minWidth: 150,
    padding: theme.spacing(1, 2),
    cursor: "pointer",
    backgroundColor: "transparent",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
      width: "100%"
    }
  },
  wabaSubTabButtonActive: {
    backgroundColor: "rgba(18, 140, 126, 0.14)",
    color: theme.palette.primary.main
  },
  wabaItem: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.22)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(11, 20, 30, 0.64)"
        : "rgba(255, 255, 255, 0.85)"
  },
  card: {
    padding: theme.spacing(2),
    borderRadius: 16,
    height: "100%",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.18)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.92)"
        : theme.palette.background.paper,
    cursor: "pointer",
    transition:
      "box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease",
    "&:hover": {
      borderColor: "rgba(18, 140, 126, 0.32)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 14px 32px rgba(0, 0, 0, 0.28)"
          : "0 12px 28px rgba(15, 23, 42, 0.08)",
      transform: "translateY(-1px)"
    }
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  cardTitle: {
    fontWeight: 700
  },
  cardText: {
    color: theme.palette.text.secondary
  },
  dialogContent: {
    minWidth: 560,
    [theme.breakpoints.down("sm")]: {
      minWidth: 0
    }
  },
  dialogDescription: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  dialogGrid: {
    display: "grid",
    gap: theme.spacing(1.5),
    gridTemplateColumns: "1fr 1fr",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  helperBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: "1px dashed rgba(148, 163, 184, 0.35)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(11, 20, 30, 0.74)"
        : "rgba(248, 250, 252, 0.8)"
  },
  templateHintRow: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(-0.5)
  },
  templateHintIcon: {
    padding: 4
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 700
  },
  actionFormBox: {
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: 12,
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.72)"
        : theme.palette.background.paper
  },
  tableWrap: {
    marginTop: theme.spacing(1.5),
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.92)"
        : theme.palette.background.paper
  },
  actionName: {
    fontWeight: 700
  },
  userChips: {
    display: "flex",
    gap: theme.spacing(0.75),
    flexWrap: "wrap"
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5)
  },
  emptyState: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5)
  },
  permissionActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    justifyContent: "flex-end"
  },
  wabaTemplateSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.22)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(11, 20, 30, 0.62)"
        : "rgba(255, 255, 255, 0.82)"
  },
  wabaTemplateGrid: {
    marginTop: theme.spacing(1.25),
    display: "grid",
    gap: theme.spacing(1.25),
    gridTemplateColumns: "1fr 1fr",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  wabaButtonsEditor: {
    marginTop: theme.spacing(1.25),
    display: "grid",
    gap: theme.spacing(1)
  },
  wabaButtonRow: {
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "170px 1fr 1fr auto",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  wabaTemplateActions: {
    marginTop: theme.spacing(1.25),
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    flexWrap: "wrap"
  },
  wabaTemplateList: {
    marginTop: theme.spacing(1.25),
    maxHeight: 420,
    overflow: "auto"
  },
  wabaTemplateCards: {
    display: "grid",
    gap: theme.spacing(1.25),
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))"
  },
  wabaTemplateRow: {
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: 12,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.92)"
        : "rgba(255,255,255,0.9)",
    padding: theme.spacing(1.25)
  },
  wabaTemplateMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.5)
  },
  wabaTemplateHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1)
  },
  dangerIconButton: {
    color: "#b91c1c"
  },
  callEventCard: {
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(45, 212, 191, 0.14)"
        : "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: 12,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.92)"
        : "rgba(255,255,255,0.94)",
    padding: theme.spacing(1.25),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.6)
  },
  callEventMeta: {
    display: "flex",
    gap: theme.spacing(0.75),
    flexWrap: "wrap"
  },
  callEventFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  statusChipApproved: {
    backgroundColor: "rgba(16, 185, 129, 0.16)",
    color: "#065f46",
    fontWeight: 700
  },
  statusChipRejected: {
    backgroundColor: "rgba(239, 68, 68, 0.14)",
    color: "#991b1b",
    fontWeight: 700
  },
  statusChipPending: {
    backgroundColor: "rgba(245, 158, 11, 0.18)",
    color: "#92400e",
    fontWeight: 700
  },
  monoBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: "1px solid rgba(148, 163, 184, 0.28)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(3, 7, 18, 0.42)"
        : "rgba(15, 23, 42, 0.04)",
    fontFamily: "Consolas, 'Courier New', monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.82rem"
  },
  mappingSection: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25),
    borderRadius: 12,
    border: "1px solid rgba(148, 163, 184, 0.24)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(11, 20, 30, 0.72)"
        : "rgba(248, 250, 252, 0.65)"
  },
  mappingGrid: {
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "1fr 1fr 1fr 0.7fr auto",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  mappingList: {
    marginTop: theme.spacing(1),
    display: "grid",
    gap: theme.spacing(0.75)
  },
  mappingRow: {
    border: "1px solid rgba(148, 163, 184, 0.24)",
    borderRadius: 10,
    padding: theme.spacing(0.75, 1),
    display: "grid",
    gap: theme.spacing(0.5),
    gridTemplateColumns: "minmax(120px, 180px) 1fr auto",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  mappingMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem"
  },
  mappingQuickList: {
    marginTop: theme.spacing(1),
    maxHeight: 220,
    overflow: "auto",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    borderRadius: 10,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13, 24, 34, 0.96)"
        : "#fff"
  },
  mappingQuickRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.6, 0.8),
    borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
    "&:last-child": {
      borderBottom: "none"
    }
  }
}));

const ApiTools = () => {
  const classes = useStyles();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("api-tools");
  const [wabaLoading, setWabaLoading] = useState(false);
  const [wabaSaving, setWabaSaving] = useState(false);
  const [wabaTesting, setWabaTesting] = useState(false);
  const [wabaSubTab, setWabaSubTab] = useState("config");
  const [wabaConnections, setWabaConnections] = useState([]);
  const [wabaSelectedWhatsappId, setWabaSelectedWhatsappId] = useState("");
  // A parte de Templates tem a sua propria escolha de conexao: inclui as da
  // Gupshup, que nao tem as outras partes desta aba (credenciais, chamadas).
  const [templateConnections, setTemplateConnections] = useState([]);
  const [templateWhatsappId, setTemplateWhatsappId] = useState("");
  const [wabaForm, setWabaForm] = useState(defaultWabaForm);
  const [wabaTestResult, setWabaTestResult] = useState(null);
  const [wabaTemplatesLoading, setWabaTemplatesLoading] = useState(false);
  const [wabaTemplatesCreating, setWabaTemplatesCreating] = useState(false);
  const [wabaTemplates, setWabaTemplates] = useState([]);
  const [wabaCallsLoading, setWabaCallsLoading] = useState(false);
  const [wabaCalls, setWabaCalls] = useState([]);
  const [wabaTemplatePermissionsLoading, setWabaTemplatePermissionsLoading] = useState(false);
  const [wabaTemplatePermissions, setWabaTemplatePermissions] = useState(null);
  const [wabaTemplateForm, setWabaTemplateForm] = useState(defaultWabaTemplateForm);
  const [wabaCallingLoading, setWabaCallingLoading] = useState(false);
  const [wabaCallingSaving, setWabaCallingSaving] = useState(false);
  const [wabaCallingForm, setWabaCallingForm] = useState(defaultWabaCallingForm);
  const [wabaCallingResult, setWabaCallingResult] = useState(null);
  const [activeWindow, setActiveWindow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [actionForm, setActionForm] = useState(defaultActionForm);
  const [selectedPermissionActionId, setSelectedPermissionActionId] = useState("");
  const [permissionUserIds, setPermissionUserIds] = useState([]);
  const [testingAction, setTestingAction] = useState(null);
  const [testForm, setTestForm] = useState(buildDefaultTestForm());
  const [testResult, setTestResult] = useState(null);
  const [testMappings, setTestMappings] = useState([]);
  const [mappingDraft, setMappingDraft] = useState(buildDefaultMappingDraft());

  const dialogMeta = useMemo(() => {
    const map = {
      http: {
        title: i18n.t("apiTools.httpTitle"),
        description:
          i18n.t("apiTools.introText")
      },
      permissions: {
        title: i18n.t("apiTools.permissionsTitle"),
        description:
          i18n.t("apiTools.permissionsText")
      },
      audit: {
        title: i18n.t("apiTools.auditTitle"),
        description:
          i18n.t("apiTools.auditText")
      }
    };

    return map[activeWindow] || { title: "", description: "" };
  }, [activeWindow]);

  const testResponseJson = testResult?.response?.jsonBody || null;
  const testResponsePathOptions = useMemo(() => {
    if (!testResponseJson) {
      return [];
    }

    return collectJsonPathOptions(testResponseJson, []).slice(0, 250);
  }, [testResponseJson]);

  const serializedTestMappings = useMemo(
    () => stringifyResponseMappings(testMappings),
    [testMappings]
  );
  const testMappingsTemplatePreview = useMemo(
    () => formatMappedVariablesTemplatePreview(testMappings),
    [testMappings]
  );
  const actionFormMappingsTemplatePreview = useMemo(
    () =>
      formatMappedVariablesTemplatePreview(
        parseResponseMappingJson(actionForm.responseMappingJson)
      ),
    [actionForm.responseMappingJson]
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [actionsResponse, usersResponse] = await Promise.all([
        api.get("/api-tools/actions"),
        api.get("/api-tools/users")
      ]);
      setActions(actionsResponse.data || []);
      setUsers(usersResponse.data || []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const mapWhatsappToWabaForm = whatsapp => ({
    wabaEnabled: Boolean(whatsapp?.wabaEnabled),
    wabaPhoneNumberId: whatsapp?.wabaPhoneNumberId || "",
    wabaBusinessAccountId: whatsapp?.wabaBusinessAccountId || "",
    wabaAccessToken: whatsapp?.wabaAccessToken || "",
    wabaVerifyToken: whatsapp?.wabaVerifyToken || "",
    wabaWebhookUrl: whatsapp?.wabaWebhookUrl || ""
  });

  const loadWabaConnections = async () => {
    setWabaLoading(true);
    try {
      const { data } = await api.get("/whatsapp/");
      const connections = (Array.isArray(data) ? data : []).filter(
        item => String(item?.connectionType || "baileys") === "waba"
      );
      setWabaConnections(connections);
      const forTemplates = (Array.isArray(data) ? data : []).filter(item =>
        ["waba", "gupshup"].includes(String(item?.connectionType || ""))
      );
      setTemplateConnections(forTemplates);
      setTemplateWhatsappId(previous =>
        forTemplates.some(item => String(item.id) === String(previous))
          ? String(previous)
          : forTemplates.length
          ? String(forTemplates[0].id)
          : ""
      );

      if (!connections.length) {
        setWabaSelectedWhatsappId("");
        setWabaForm(defaultWabaForm);
        setWabaTemplates([]);
        setWabaCallingForm(defaultWabaCallingForm);
        setWabaCallingResult(null);
        return;
      }

      const selected =
        connections.find(
          item => String(item.id) === String(wabaSelectedWhatsappId || "")
        ) || connections[0];

      setWabaSelectedWhatsappId(String(selected.id));
      setWabaForm(mapWhatsappToWabaForm(selected));
      await loadWabaTemplates(String(selected.id));
      await loadWabaTemplatePermissions(String(selected.id));
      await loadWabaCalls(String(selected.id));
      await loadWabaCallingSettings(String(selected.id));
    } catch (error) {
      toastError(error);
    } finally {
      setWabaLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "waba") {
      loadWabaConnections();
    }
  }, [activeTab]);

  const templateConnection = templateConnections.find(
    item => String(item.id) === String(templateWhatsappId || "")
  );
  const isGupshupTemplate =
    String(templateConnection?.connectionType || "") === "gupshup";

  useEffect(() => {
    if (activeTab !== "waba" || wabaSubTab !== "templates") {
      return;
    }

    if (!templateWhatsappId) {
      setWabaTemplates([]);
      return;
    }

    loadWabaTemplates(templateWhatsappId);
    if (isGupshupTemplate) {
      setWabaTemplatePermissions(null);
    } else {
      loadWabaTemplatePermissions(templateWhatsappId);
    }
  }, [activeTab, wabaSubTab, templateWhatsappId, isGupshupTemplate]);

  useEffect(() => {
    if (activeTab !== "waba" || wabaSubTab !== "calls") {
      return;
    }

    if (!wabaSelectedWhatsappId) {
      return;
    }

    loadWabaCalls(wabaSelectedWhatsappId);
  }, [activeTab, wabaSubTab, wabaSelectedWhatsappId]);

  const handleWabaSelectChange = value => {
    const selected = wabaConnections.find(
      item => String(item.id) === String(value || "")
    );
    setWabaSelectedWhatsappId(String(value || ""));
    setWabaForm(mapWhatsappToWabaForm(selected));
    setWabaTestResult(null);
    loadWabaTemplates(String(value || ""));
    loadWabaTemplatePermissions(String(value || ""));
    loadWabaCalls(String(value || ""));
    loadWabaCallingSettings(String(value || ""));
  };

  const handleWabaFieldChange = (field, value) => {
    setWabaForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const mapMetaCallingSettingsToForm = data => {
    const calling = data?.calling || {};
    const sip = calling?.sip || {};
    const firstServer = Array.isArray(sip?.servers) ? sip.servers[0] || {} : {};

    return {
      status: String(calling?.status || "ENABLED").toUpperCase(),
      callIconVisibility: String(calling?.call_icon_visibility || "DEFAULT").toUpperCase(),
      callbackPermissionStatus: String(
        calling?.callback_permission_status || "ENABLED"
      ).toUpperCase(),
      sipEnabled: String(sip?.status || "DISABLED").toUpperCase() === "ENABLED",
      sipHostname: String(firstServer?.hostname || "").trim(),
      sipPort: String(firstServer?.port || "15060").trim() || "15060"
    };
  };

  const loadWabaCallingSettings = async whatsappId => {
    if (!whatsappId) {
      setWabaCallingForm(defaultWabaCallingForm);
      setWabaCallingResult(null);
      return;
    }

    setWabaCallingLoading(true);
    try {
      const { data } = await api.get(
        `/whatsapp/${whatsappId}/waba/calling/settings`
      );
      setWabaCallingForm(mapMetaCallingSettingsToForm(data));
      setWabaCallingResult(data || null);
    } catch (error) {
      toastError(error);
      setWabaCallingForm(defaultWabaCallingForm);
      setWabaCallingResult(null);
    } finally {
      setWabaCallingLoading(false);
    }
  };

  const handleWabaCallingFieldChange = (field, value) => {
    setWabaCallingForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveWabaCallingSettings = async () => {
    if (!wabaSelectedWhatsappId) {
      toast.error("Selecione uma conexão para salvar call settings.");
      return;
    }

    setWabaCallingSaving(true);
    try {
      const payload = {
        calling: {
          status: String(wabaCallingForm.status || "ENABLED").toUpperCase(),
          call_icon_visibility: String(
            wabaCallingForm.callIconVisibility || "DEFAULT"
          ).toUpperCase(),
          callback_permission_status: String(
            wabaCallingForm.callbackPermissionStatus || "ENABLED"
          ).toUpperCase(),
          sip: {
            status: wabaCallingForm.sipEnabled ? "ENABLED" : "DISABLED",
            servers: wabaCallingForm.sipEnabled
              ? [
                  {
                    hostname: String(wabaCallingForm.sipHostname || "").trim(),
                    port: Number(wabaCallingForm.sipPort || 15060)
                  }
                ]
              : []
          }
        }
      };

      if (wabaCallingForm.sipEnabled && !payload.calling.sip.servers[0].hostname) {
        toast.error("Informe o hostname SIP para habilitar SIP.");
        setWabaCallingSaving(false);
        return;
      }

      const { data } = await api.post(
        `/whatsapp/${wabaSelectedWhatsappId}/waba/calling/settings`,
        payload
      );
      setWabaCallingResult(data || null);
      toast.success("Call settings salvas na Meta.");
      await loadWabaCallingSettings(wabaSelectedWhatsappId);
    } catch (error) {
      toastError(error);
    } finally {
      setWabaCallingSaving(false);
    }
  };

  const loadWabaTemplates = async whatsappId => {
    if (!whatsappId) {
      setWabaTemplates([]);
      return;
    }

    setWabaTemplatesLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/${whatsappId}/waba/templates/manage`);
      setWabaTemplates(Array.isArray(data?.templates) ? data.templates : []);
    } catch (error) {
      toastError(error);
      setWabaTemplates([]);
    } finally {
      setWabaTemplatesLoading(false);
    }
  };

  const loadWabaCalls = async whatsappId => {
    if (!whatsappId) {
      setWabaCalls([]);
      return;
    }

    setWabaCallsLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/${whatsappId}/waba/calls`, {
        params: { limit: 100 }
      });
      setWabaCalls(Array.isArray(data?.calls) ? data.calls : []);
    } catch (error) {
      toastError(error);
      setWabaCalls([]);
    } finally {
      setWabaCallsLoading(false);
    }
  };

  const loadWabaTemplatePermissions = async whatsappId => {
    if (!whatsappId) {
      setWabaTemplatePermissions(null);
      return;
    }

    setWabaTemplatePermissionsLoading(true);
    try {
      const { data } = await api.get(
        `/whatsapp/${whatsappId}/waba/templates/permissions`
      );
      setWabaTemplatePermissions(data || null);
    } catch (error) {
      toastError(error);
      setWabaTemplatePermissions(null);
    } finally {
      setWabaTemplatePermissionsLoading(false);
    }
  };

  const handleWabaTemplateFieldChange = (field, value) => {
    setWabaTemplateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWabaTemplateButton = () => {
    setWabaTemplateForm(prev => {
      if ((prev.buttons || []).length >= 3) {
        toast.warn("A Meta permite no máximo 3 botoes por template.");
        return prev;
      }

      return {
        ...prev,
        buttons: [
          ...(prev.buttons || []),
          { type: "QUICK_REPLY", text: "", url: "", phoneNumber: "" }
        ]
      };
    });
  };

  const updateWabaTemplateButton = (index, field, value) => {
    setWabaTemplateForm(prev => ({
      ...prev,
      buttons: (prev.buttons || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeWabaTemplateButton = index => {
    setWabaTemplateForm(prev => ({
      ...prev,
      buttons: (prev.buttons || []).filter((_, i) => i !== index)
    }));
  };

  const createWabaTemplate = async () => {
    if (!templateWhatsappId) {
      toast.error("Selecione uma conexão para criar template.");
      return;
    }

    if (!isGupshupTemplate && wabaTemplatePermissions?.manageTemplates?.ok === false) {
      toast.error(
        "Token sem permissão de gerenciamento de templates. Revise o checklist de permissões."
      );
      return;
    }

    setWabaTemplatesCreating(true);
    try {
      await api.post(`/whatsapp/${templateWhatsappId}/waba/templates`, {
        name: String(wabaTemplateForm.name || "").trim(),
        language: String(wabaTemplateForm.language || "").trim() || "pt_BR",
        category: String(wabaTemplateForm.category || "").trim().toUpperCase(),
        bodyText: String(wabaTemplateForm.bodyText || "").trim(),
        headerText: String(wabaTemplateForm.headerText || "").trim() || null,
        footerText: String(wabaTemplateForm.footerText || "").trim() || null,
        buttons: (Array.isArray(wabaTemplateForm.buttons)
          ? wabaTemplateForm.buttons
          : []
        ).map(item => ({
          type: String(item?.type || "").trim().toUpperCase(),
          text: String(item?.text || "").trim(),
          url: String(item?.url || "").trim() || null,
          phoneNumber: String(item?.phoneNumber || "").trim() || null
        })),
        bodyExamples: templateVariableIndexes(wabaTemplateForm.bodyText).map(
          index => String((wabaTemplateForm.bodyExamples || [])[index - 1] || "").trim()
        )
      });

      toast.success(
        isGupshupTemplate
          ? "Template enviado para aprovação da Meta pela Gupshup."
          : "Template enviado para aprovação na Meta."
      );
      setWabaTemplateForm(defaultWabaTemplateForm);
      await loadWabaTemplates(templateWhatsappId);
    } catch (error) {
      toastError(error);
    } finally {
      setWabaTemplatesCreating(false);
    }
  };

  const deleteWabaTemplate = async item => {
    if (!templateWhatsappId) {
      toast.error("Selecione uma conexão.");
      return;
    }

    if (!isGupshupTemplate && wabaTemplatePermissions?.manageTemplates?.ok === false) {
      toast.error(
        "Token sem permissão de gerenciamento de templates. Revise o checklist de permissões."
      );
      return;
    }

    const name = String(item?.name || "").trim();
    const language = String(item?.language || "").trim();
    if (!name || !language) {
      toast.error("Template inválido para exclusão.");
      return;
    }

    const confirmed = window.confirm(
      isGupshupTemplate
        ? `Excluir o template "${name}" na Gupshup? Na Gupshup a exclusão é pelo nome: sai em todos os idiomas.`
        : `Excluir template "${name}" (${language}) na Meta?`
    );
    if (!confirmed) {
      return;
    }

    try {
      await api.delete(`/whatsapp/${templateWhatsappId}/waba/templates`, {
        params: { name, language }
      });
      toast.success("Template excluído.");
      await loadWabaTemplates(templateWhatsappId);
    } catch (error) {
      toastError(error);
    }
  };

  const saveWabaSettings = async () => {
    if (!wabaSelectedWhatsappId) {
      toast.error("Selecione uma conexão para salvar.");
      return;
    }

    setWabaSaving(true);
    try {
      await api.put(`/whatsapp/${wabaSelectedWhatsappId}/waba`, {
        wabaEnabled: Boolean(wabaForm.wabaEnabled),
        wabaPhoneNumberId: String(wabaForm.wabaPhoneNumberId || "").trim() || null,
        wabaBusinessAccountId:
          String(wabaForm.wabaBusinessAccountId || "").trim() || null,
        wabaAccessToken: String(wabaForm.wabaAccessToken || "").trim() || null,
        wabaVerifyToken: String(wabaForm.wabaVerifyToken || "").trim() || null,
        wabaWebhookUrl: String(wabaForm.wabaWebhookUrl || "").trim() || null
      });
      toast.success("Configuração WABA salva.");
      await loadWabaConnections();
    } catch (error) {
      toastError(error);
    } finally {
      setWabaSaving(false);
    }
  };

  const testWabaConnection = async () => {
    if (!wabaSelectedWhatsappId) {
      toast.error("Selecione uma conexão para testar.");
      return;
    }

    setWabaTesting(true);
    try {
      const { data } = await api.post(`/whatsapp/${wabaSelectedWhatsappId}/waba/test`);
      setWabaTestResult(data);
      if (data?.ok) {
        toast.success("Conexão WABA validada com sucesso.");
      } else {
        toast.warn("A válidação WABA retornou erro.");
      }
      await loadWabaConnections();
    } catch (error) {
      toastError(error);
    } finally {
      setWabaTesting(false);
    }
  };

  const generateWabaVerifyToken = () => {
    try {
      const bytes = new Uint8Array(24);
      window.crypto.getRandomValues(bytes);
      const token = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
      handleWabaFieldChange("wabaVerifyToken", token);
      toast.success("Verify Token gerado.");
    } catch (error) {
      toast.error("Não foi possível gerar o token no navegador.");
    }
  };

  useEffect(() => {
    if (activeWindow !== "permissions") {
      return;
    }

    if (!actions.length) {
      setSelectedPermissionActionId("");
      setPermissionUserIds([]);
      return;
    }

    if (!selectedPermissionActionId) {
      const firstActionId = String(actions[0].id);
      setSelectedPermissionActionId(firstActionId);
      setPermissionUserIds(actions[0].allowedUserIds || []);
      return;
    }

    const selected = actions.find(
      action => String(action.id) === String(selectedPermissionActionId)
    );

    if (!selected) {
      const firstActionId = String(actions[0].id);
      setSelectedPermissionActionId(firstActionId);
      setPermissionUserIds(actions[0].allowedUserIds || []);
      return;
    }

    setPermissionUserIds(selected.allowedUserIds || []);
  }, [activeWindow, actions, selectedPermissionActionId]);

  const closeDialog = () => {
    setActiveWindow(null);
    setActionForm(defaultActionForm);
  };

  const handleActionFieldChange = (field, value) => {
    setActionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditAction = action => {
    setActionForm({
      id: action.id,
      name: action.name || "",
      description: action.description || "",
      method: action.method || "POST",
      endpoint: action.endpoint || "",
      timeoutMs: action.timeoutMs || 10000,
      headersJson: action.headersJson || "",
      bodyTemplate: action.bodyTemplate || "",
      resultTemplate: action.resultTemplate || "",
      responseMappingJson: action.responseMappingJson || "",
      isActive: Boolean(action.isActive),
      requiresDocument: Boolean(action.requiresDocument),
      requiresNote: Boolean(action.requiresNote),
      noteLabel: action.noteLabel || "",
      resultMode: action.resultMode || "display",
      allowedUserIds: action.allowedUserIds || []
    });
    setActiveWindow("http");
  };

  const resetActionForm = () => {
    setActionForm(defaultActionForm);
  };

  const saveAction = async () => {
    const payload = {
      name: actionForm.name.trim(),
      description: actionForm.description.trim() || null,
      method: actionForm.method,
      endpoint: actionForm.endpoint.trim(),
      timeoutMs: Number(actionForm.timeoutMs) || 10000,
      headersJson: actionForm.headersJson || null,
      bodyTemplate: actionForm.bodyTemplate || null,
      resultTemplate: actionForm.resultTemplate || null,
      responseMappingJson: actionForm.responseMappingJson || null,
      isActive: Boolean(actionForm.isActive),
      requiresDocument: Boolean(actionForm.requiresDocument),
      requiresNote: Boolean(actionForm.requiresNote),
      noteLabel: String(actionForm.noteLabel || "").trim() || null,
      resultMode: String(actionForm.resultMode || "display"),
      allowedUserIds: actionForm.allowedUserIds || []
    };

    if (!payload.name || !payload.endpoint) {
      toast.error("Preencha nome e endpoint da ação.");
      return;
    }

    if (payload.responseMappingJson) {
      try {
        JSON.parse(payload.responseMappingJson);
      } catch (error) {
        toast.error("JSON de mapeamento da resposta inválido.");
        return;
      }
    }

    setSaving(true);
    try {
      let response;
      if (actionForm.id) {
        response = await api.put(`/api-tools/actions/${actionForm.id}`, payload);
        toast.success("Ação atualizada com sucesso.");
      } else {
        response = await api.post("/api-tools/actions", payload);
        toast.success("Ação criada com sucesso.");
      }

      const savedAction = response.data;
      setActions(prev => {
        const index = prev.findIndex(item => item.id === savedAction.id);
        if (index >= 0) {
          const next = [...prev];
          next[index] = savedAction;
          return next.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        }

        return [...prev, savedAction].sort((a, b) =>
          String(a.name).localeCompare(String(b.name))
        );
      });
      resetActionForm();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const deleteAction = async actionId => {
    const confirmDelete = window.confirm(
      "Confirma remover esta ação HTTP? Esta ação não podera ser desfeita."
    );

    if (!confirmDelete) {
      return;
    }

    setSaving(true);
    try {
      await api.delete(`/api-tools/actions/${actionId}`);
      setActions(prev => prev.filter(item => item.id !== actionId));
      if (String(selectedPermissionActionId) === String(actionId)) {
        setSelectedPermissionActionId("");
        setPermissionUserIds([]);
      }
      if (Number(actionForm.id) === Number(actionId)) {
        resetActionForm();
      }
      toast.success("Ação removida com sucesso.");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const savePermissions = async () => {
    if (!selectedPermissionActionId) {
      toast.error("Selecione uma ação para configurar permissões.");
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(
        `/api-tools/actions/${selectedPermissionActionId}/permissions`,
        {
          allowedUserIds: permissionUserIds
        }
      );
      const updatedAction = response.data;
      setActions(prev => {
        const index = prev.findIndex(item => item.id === updatedAction.id);
        if (index < 0) {
          return prev;
        }
        const next = [...prev];
        next[index] = updatedAction;
        return next;
      });
      toast.success("Permissões atualizadas.");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = action => {
    setTestingAction(action);
    setTestForm(buildDefaultTestForm());
    setTestResult(null);
    setTestMappings(parseResponseMappingJson(action?.responseMappingJson));
    setMappingDraft(buildDefaultMappingDraft());
  };

  const closeTestDialog = () => {
    setTestingAction(null);
    setTestResult(null);
    setTestMappings([]);
    setMappingDraft(buildDefaultMappingDraft());
  };

  const handleMappingDraftFieldChange = (field, value) => {
    setMappingDraft(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addMappingItem = mapping => {
    const key = sanitizeVariableKey(mapping?.key);
    if (!key) {
      toast.error("Informe um nome válido para a variável.");
      return;
    }

    const source = normalizeMappingSource(mapping?.source);
    const path = String(mapping?.path || "").trim();
    if (source === "response.json" && !path) {
      toast.error("Selecione o caminho JSON da resposta.");
      return;
    }

    const defaultValue = parseDefaultValueInput(mapping?.defaultValue);

    setTestMappings(prev => {
      const filtered = (Array.isArray(prev) ? prev : []).filter(
        item => String(item.key) !== key
      );

      const next = {
        key,
        source
      };

      if (source === "response.json") {
        next.path = path;
      }

      if (defaultValue !== undefined) {
        next.defaultValue = defaultValue;
      }

      return [...filtered, next];
    });
  };

  const addMappingFromDraft = () => {
    addMappingItem(mappingDraft);
    setMappingDraft(buildDefaultMappingDraft());
  };

  const addQuickMappingFromPath = path => {
    const pathValue = String(path || "").trim();
    if (!pathValue || pathValue === "$") {
      return;
    }

    const suggestedKey = pathToVariableKey(pathValue) || `var_${Date.now()}`;
    addMappingItem({
      key: suggestedKey,
      source: "response.json",
      path: pathValue
    });
  };

  const removeMappingItem = key => {
    setTestMappings(prev =>
      (Array.isArray(prev) ? prev : []).filter(item => String(item.key) !== String(key))
    );
  };

  const saveMappingsToAction = async () => {
    if (!testingAction?.id) {
      return;
    }

    const payload = {
      name: String(testingAction.name || "").trim(),
      description: String(testingAction.description || "").trim() || null,
      method: String(testingAction.method || "POST"),
      endpoint: String(testingAction.endpoint || "").trim(),
      timeoutMs: Number(testingAction.timeoutMs) || 10000,
      headersJson: testingAction.headersJson || null,
      bodyTemplate: testingAction.bodyTemplate || null,
      resultTemplate: testingAction.resultTemplate || null,
      responseMappingJson: serializedTestMappings || null,
      isActive: testingAction.isActive !== false,
      requiresDocument: Boolean(testingAction.requiresDocument),
      resultMode: String(testingAction.resultMode || "display"),
      allowedUserIds: normalizeIdList(testingAction.allowedUserIds)
    };

    if (!payload.name || !payload.endpoint) {
      toast.error("Não foi possível salvar: ação sem nome ou endpoint.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.put(
        `/api-tools/actions/${testingAction.id}`,
        payload
      );

      setActions(prev => {
        const index = prev.findIndex(item => item.id === data.id);
        if (index < 0) {
          return prev;
        }

        const next = [...prev];
        next[index] = data;
        return next.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      });

      setTestingAction(data);
      if (Number(actionForm.id) === Number(data.id)) {
        setActionForm(prev => ({
          ...prev,
          responseMappingJson: data.responseMappingJson || ""
        }));
      }

      toast.success("Mapeamento salvo na ação.");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const runActionTest = async () => {
    if (!testingAction?.id) {
      return;
    }

    let variables = {};
    try {
      variables = JSON.parse(String(testForm?.variablesJson || "{}"));
      if (variables === null || typeof variables !== "object" || Array.isArray(variables)) {
        toast.error("Variáveis devem ser um objeto JSON.");
        return;
      }
    } catch (error) {
      toast.error("JSON de variáveis inválido.");
      return;
    }

    const variablesText = JSON.stringify(variables);
    const hasContactDocumentPlaceholderInVariables =
      variablesText.includes("{{contact.document}}") ||
      variablesText.includes("{{{contact.document}}}");
    const hasContactReference =
      Boolean(testForm?.contactId) || Boolean(testForm?.ticketId);

    if (hasContactDocumentPlaceholderInVariables && !hasContactReference) {
      toast.error(
        "No campo Variáveis JSON informe valor literal. Use {{contact.document}} apenas no Body template."
      );
      return;
    }

    const payload = {
      ticketId: testForm?.ticketId ? Number(testForm.ticketId) : undefined,
      contactId: testForm?.contactId ? Number(testForm.contactId) : undefined,
      variables,
      responseMappingJson: serializedTestMappings || undefined
    };

    setSaving(true);
    try {
      const { data } = await api.post(
        `/api-tools/actions/${testingAction.id}/test`,
        payload
      );
      setTestResult(data);
      toast.success("Teste executado.");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const selectedPermissionAction = actions.find(
    action => String(action.id) === String(selectedPermissionActionId)
  );

  const getStatusChipClass = status => {
    const normalizedStatus = String(status || "").trim().toUpperCase();
    if (normalizedStatus === "APPROVED") {
      return classes.statusChipApproved;
    }
    if (normalizedStatus === "REJECTED") {
      return classes.statusChipRejected;
    }
    return classes.statusChipPending;
  };

  const formatResultModeLabel = mode => {
    const normalized = String(mode || "display");
    if (normalized === "send_conversation") {
      return "Enviar na conversa";
    }
    if (normalized === "open_redirect") {
      return "Abrir/Redirecionar";
    }
    return "Exibir";
  };

  const formatCallDate = value => {
    if (!value) {
      return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }

    return parsed.toLocaleString("pt-BR");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>API Tools</Title>
      </MainHeader>

      <Paper className={classes.tabsPaper} variant="outlined">
        <div className={classes.topTabsRow}>
          <div
            className={`${classes.topTabButton} ${
              activeTab === "api-tools" ? classes.topTabButtonActive : ""
            }`}
            onClick={() => setActiveTab("api-tools")}
          >
            API Tools
          </div>
          <div
            className={`${classes.topTabButton} ${
              activeTab === "waba" ? classes.topTabButtonActive : ""
            }`}
            onClick={() => setActiveTab("waba")}
          >
            WABA
          </div>
        </div>
      </Paper>

      <Paper className={classes.pagePaper} variant="outlined">
        {activeTab === "api-tools" ? (
          <>
            <Paper className={classes.introCard} variant="outlined">
              <Typography className={classes.introTitle} variant="h6">
                {i18n.t("apiTools.introTitle")}
              </Typography>
              <Typography className={classes.introText} variant="body2">
                {i18n.t("apiTools.introText")}
              </Typography>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper
                  className={classes.card}
                  variant="outlined"
                  onClick={() => setActiveWindow("http")}
                >
                  <div className={classes.cardHeader}>
                    <HttpOutlinedIcon color="primary" />
                    <Typography className={classes.cardTitle}>{i18n.t("apiTools.httpTitle")}</Typography>
                  </div>
                  <Typography className={classes.cardText} variant="body2">
                    {i18n.t("apiTools.httpText")}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  className={classes.card}
                  variant="outlined"
                  onClick={() => setActiveWindow("permissions")}
                >
                  <div className={classes.cardHeader}>
                    <SecurityOutlinedIcon color="primary" />
                    <Typography className={classes.cardTitle}>{i18n.t("apiTools.permissionsTitle")}</Typography>
                  </div>
                  <Typography className={classes.cardText} variant="body2">
                    {i18n.t("apiTools.permissionsText")}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  className={classes.card}
                  variant="outlined"
                  onClick={() => setActiveWindow("audit")}
                >
                  <div className={classes.cardHeader}>
                    <BuildOutlinedIcon color="primary" />
                    <Typography className={classes.cardTitle}>{i18n.t("apiTools.auditTitle")}</Typography>
                  </div>
                  <Typography className={classes.cardText} variant="body2">
                    {i18n.t("apiTools.auditText")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </>
        ) : (
          <Paper className={classes.introCard} variant="outlined">
            <Typography className={classes.introTitle} variant="h6">
              WABA (WhatsApp Business API)
            </Typography>
            <Typography className={classes.introText} variant="body2">
              {i18n.t("apiTools.wabaIntro")}
            </Typography>
            {wabaLoading ? (
              <div className={classes.loadingRow}>
                <CircularProgress size={18} />
                <span>{i18n.t("apiTools.loadingConnections")}</span>
              </div>
            ) : (
              <>
                {!wabaConnections.length ? (
                  <div className={classes.helperBox}>
                    <Typography variant="body2">
                      {i18n.t("apiTools.noWaba")}
                    </Typography>
                  </div>
                ) : null}

                <div className={classes.wabaSubTabs}>
                  <button
                    type="button"
                    className={`${classes.wabaSubTabButton} ${
                      wabaSubTab === "config" ? classes.wabaSubTabButtonActive : ""
                    }`}
                    onClick={() => setWabaSubTab("config")}
                  >
                    Configuração
                  </button>
                  <button
                    type="button"
                    className={`${classes.wabaSubTabButton} ${
                      wabaSubTab === "templates" ? classes.wabaSubTabButtonActive : ""
                    }`}
                    onClick={() => setWabaSubTab("templates")}
                  >
                    Template
                  </button>
                  <button
                    type="button"
                    className={`${classes.wabaSubTabButton} ${
                      wabaSubTab === "calls" ? classes.wabaSubTabButtonActive : ""
                    }`}
                    onClick={() => setWabaSubTab("calls")}
                  >
                    {i18n.t("apiTools.calls")}
                  </button>
                </div>

                {wabaSubTab === "config" ? (
                  <>
                    <div className={classes.wabaRoadmap}>
                      <TextField
                        select
                        variant="outlined"
                        label={i18n.t("apiTools.whatsappConnection")}
                        value={wabaSelectedWhatsappId}
                        onChange={event => handleWabaSelectChange(event.target.value)}
                        fullWidth
                        disabled={!wabaConnections.length}
                      >
                        {wabaConnections.map(item => (
                          <MenuItem key={item.id} value={String(item.id)}>
                            {item.name || `Conexão #${item.id}`}
                          </MenuItem>
                        ))}
                      </TextField>

                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={Boolean(wabaForm.wabaEnabled)}
                            onChange={event =>
                              handleWabaFieldChange("wabaEnabled", event.target.checked)
                            }
                            disabled={!wabaConnections.length}
                          />
                        }
                        label={i18n.t("apiTools.enableWaba")}
                      />

                      <div className={classes.dialogGrid}>
                        <TextField
                          variant="outlined"
                          label="Phone Number ID"
                          value={wabaForm.wabaPhoneNumberId}
                          onChange={event =>
                            handleWabaFieldChange("wabaPhoneNumberId", event.target.value)
                          }
                          disabled={!wabaConnections.length}
                          fullWidth
                        />
                        <TextField
                          variant="outlined"
                          label="Business Account ID"
                          value={wabaForm.wabaBusinessAccountId}
                          onChange={event =>
                            handleWabaFieldChange(
                              "wabaBusinessAccountId",
                              event.target.value
                            )
                          }
                          disabled={!wabaConnections.length}
                          fullWidth
                        />
                        <TextField
                          variant="outlined"
                          label="Verify Token"
                          value={wabaForm.wabaVerifyToken}
                          onChange={event =>
                            handleWabaFieldChange("wabaVerifyToken", event.target.value)
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  size="small"
                                  color="primary"
                                  onClick={generateWabaVerifyToken}
                                  disabled={!wabaConnections.length}
                                >
                                  {i18n.t("apiTools.generate")}
                                </Button>
                              </InputAdornment>
                            )
                          }}
                          disabled={!wabaConnections.length}
                          fullWidth
                        />
                        <TextField
                          variant="outlined"
                          label="Webhook URL (opcional)"
                          value={wabaForm.wabaWebhookUrl}
                          onChange={event =>
                            handleWabaFieldChange("wabaWebhookUrl", event.target.value)
                          }
                          disabled={!wabaConnections.length}
                          fullWidth
                        />
                      </div>

                      <TextField
                        variant="outlined"
                        label="Access Token"
                        type="password"
                        value={wabaForm.wabaAccessToken}
                        onChange={event =>
                          handleWabaFieldChange("wabaAccessToken", event.target.value)
                        }
                        disabled={!wabaConnections.length}
                        fullWidth
                      />

                      <div className={classes.permissionActions}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={testWabaConnection}
                          disabled={
                            wabaSaving || wabaTesting || !wabaSelectedWhatsappId
                          }
                        >
                          {wabaTesting ? i18n.t("apiTools.testing") : i18n.t("apiTools.testConnection")}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveWabaSettings}
                          disabled={
                            wabaSaving || wabaTesting || !wabaSelectedWhatsappId
                          }
                        >
                          {wabaSaving ? i18n.t("apiTools.saving") : i18n.t("apiTools.saveWaba")}
                        </Button>
                      </div>

                      {wabaTestResult ? (
                        <div className={classes.monoBox}>
                          {JSON.stringify(wabaTestResult, null, 2)}
                        </div>
                      ) : null}

                      <Divider />

                      <Typography variant="subtitle2" className={classes.sectionTitle}>
                        Ligações WABA (Meta /settings)
                      </Typography>

                      <div className={classes.dialogGrid}>
                        <TextField
                          select
                          variant="outlined"
                          label="Calling status"
                          value={wabaCallingForm.status}
                          onChange={event =>
                            handleWabaCallingFieldChange("status", event.target.value)
                          }
                          disabled={!wabaConnections.length || wabaCallingLoading}
                          fullWidth
                        >
                          <MenuItem value="ENABLED">ENABLED</MenuItem>
                          <MenuItem value="DISABLED">DISABLED</MenuItem>
                        </TextField>

                        <TextField
                          select
                          variant="outlined"
                          label="Visibilidade do icone"
                          value={wabaCallingForm.callIconVisibility}
                          onChange={event =>
                            handleWabaCallingFieldChange(
                              "callIconVisibility",
                              event.target.value
                            )
                          }
                          disabled={!wabaConnections.length || wabaCallingLoading}
                          fullWidth
                        >
                          <MenuItem value="DEFAULT">DEFAULT</MenuItem>
                          <MenuItem value="DISABLE_ALL">DISABLE_ALL</MenuItem>
                        </TextField>

                        <TextField
                          select
                          variant="outlined"
                          label="Callback permission"
                          value={wabaCallingForm.callbackPermissionStatus}
                          onChange={event =>
                            handleWabaCallingFieldChange(
                              "callbackPermissionStatus",
                              event.target.value
                            )
                          }
                          disabled={!wabaConnections.length || wabaCallingLoading}
                          fullWidth
                        >
                          <MenuItem value="ENABLED">ENABLED</MenuItem>
                          <MenuItem value="DISABLED">DISABLED</MenuItem>
                        </TextField>
                      </div>

                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={Boolean(wabaCallingForm.sipEnabled)}
                            onChange={event =>
                              handleWabaCallingFieldChange(
                                "sipEnabled",
                                event.target.checked
                              )
                            }
                            disabled={!wabaConnections.length || wabaCallingLoading}
                          />
                        }
                        label="Habilitar SIP para voz em tempo real"
                      />

                      <div className={classes.dialogGrid}>
                        <TextField
                          variant="outlined"
                          label="SIP hostname"
                          value={wabaCallingForm.sipHostname}
                          onChange={event =>
                            handleWabaCallingFieldChange("sipHostname", event.target.value)
                          }
                          disabled={
                            !wabaConnections.length ||
                            wabaCallingLoading ||
                            !wabaCallingForm.sipEnabled
                          }
                          fullWidth
                        />
                        <TextField
                          variant="outlined"
                          label="SIP port"
                          value={wabaCallingForm.sipPort}
                          onChange={event =>
                            handleWabaCallingFieldChange("sipPort", event.target.value)
                          }
                          disabled={
                            !wabaConnections.length ||
                            wabaCallingLoading ||
                            !wabaCallingForm.sipEnabled
                          }
                          fullWidth
                        />
                      </div>

                      <div className={classes.permissionActions}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => loadWabaCallingSettings(wabaSelectedWhatsappId)}
                          disabled={
                            !wabaSelectedWhatsappId ||
                            wabaCallingLoading ||
                            wabaCallingSaving
                          }
                        >
                          {wabaCallingLoading ? i18n.t("apiTools.loading") : "Carregar call settings"}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveWabaCallingSettings}
                          disabled={
                            !wabaSelectedWhatsappId ||
                            wabaCallingLoading ||
                            wabaCallingSaving
                          }
                        >
                          {wabaCallingSaving ? i18n.t("apiTools.saving") : "Salvar call settings"}
                        </Button>
                      </div>

                      {wabaCallingResult ? (
                        <div className={classes.monoBox}>
                          {JSON.stringify(wabaCallingResult, null, 2)}
                        </div>
                      ) : null}
                    </div>

                  </>
                ) : wabaSubTab === "templates" ? (
                  <div className={classes.wabaTemplateSection}>
                    <TextField
                      select
                      variant="outlined"
                      label="Conexão WhatsApp"
                      value={templateWhatsappId}
                      onChange={event => setTemplateWhatsappId(String(event.target.value || ""))}
                      fullWidth
                      disabled={!templateConnections.length}
                    >
                      {templateConnections.map(item => (
                        <MenuItem key={item.id} value={String(item.id)}>
                          {item.name || `Conexão #${item.id}`}
                          {String(item.connectionType) === "gupshup" ? " (Gupshup)" : " (Meta direta)"}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      Templates WABA
                    </Typography>
                    <Typography variant="body2" className={classes.introText}>
                      {isGupshupTemplate
                        ? "Conexão Gupshup: o template é criado no app da Gupshup e vai para aprovação da Meta por ela."
                        : "Visualize status de aprovação e crie novos templates para enviar à Meta."}
                    </Typography>

                    {!isGupshupTemplate && (
                    <div className={classes.helperBox}>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>
                        Checklist de permissões (Meta)
                      </Typography>
                      {wabaTemplatePermissionsLoading ? (
                        <div className={classes.loadingRow}>
                          <CircularProgress size={16} />
                          <span>Validando permissões...</span>
                        </div>
                      ) : wabaTemplatePermissions ? (
                        <div className={classes.wabaTemplateMeta}>
                          <Chip
                            size="small"
                            label={`Credenciais: ${
                              wabaTemplatePermissions?.credentials?.ok ? "OK" : "Falha"
                            }`}
                            className={
                              wabaTemplatePermissions?.credentials?.ok
                                ? classes.statusChipApproved
                                : classes.statusChipRejected
                            }
                          />
                          <Chip
                            size="small"
                            label={`Listar templates: ${
                              wabaTemplatePermissions?.listTemplates?.ok ? "OK" : "Falha"
                            }`}
                            className={
                              wabaTemplatePermissions?.listTemplates?.ok
                                ? classes.statusChipApproved
                                : classes.statusChipRejected
                            }
                          />
                          <Chip
                            size="small"
                            label={`Gerenciar templates: ${
                              wabaTemplatePermissions?.manageTemplates?.ok ? "OK" : "Falha"
                            }`}
                            className={
                              wabaTemplatePermissions?.manageTemplates?.ok
                                ? classes.statusChipApproved
                                : classes.statusChipRejected
                            }
                          />
                        </div>
                      ) : (
                        <Typography variant="caption" className={classes.introText}>
                          Sem válidação de permissões ainda.
                        </Typography>
                      )}
                      <div className={classes.permissionActions}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => loadWabaTemplatePermissions(templateWhatsappId)}
                          disabled={!templateWhatsappId || wabaTemplatePermissionsLoading}
                        >
                          Validar permissões
                        </Button>
                      </div>
                      {wabaTemplatePermissions?.manageTemplates?.message ? (
                        <Typography variant="caption" className={classes.introText}>
                          {String(wabaTemplatePermissions.manageTemplates.message)}
                        </Typography>
                      ) : null}
                    </div>
                    )}

                    <div className={classes.wabaTemplateGrid}>
                      <TextField
                        variant="outlined"
                        label="Nome interno do template"
                        placeholder="ex.: lembrete_pagamento"
                        value={wabaTemplateForm.name}
                        onChange={event =>
                          handleWabaTemplateFieldChange("name", event.target.value)
                        }
                        disabled={!templateWhatsappId}
                        fullWidth
                      />
                      <TextField
                        select
                        variant="outlined"
                        label="Categoria"
                        value={wabaTemplateForm.category}
                        onChange={event =>
                          handleWabaTemplateFieldChange("category", event.target.value)
                        }
                        disabled={!templateWhatsappId}
                        fullWidth
                      >
                        <MenuItem value="UTILITY">UTILITY</MenuItem>
                        <MenuItem value="MARKETING">MARKETING</MenuItem>
                        <MenuItem value="AUTHENTICATION">AUTHENTICATION</MenuItem>
                      </TextField>
                      <TextField
                        variant="outlined"
                        label="Idioma"
                        value={wabaTemplateForm.language}
                        onChange={event =>
                          handleWabaTemplateFieldChange("language", event.target.value)
                        }
                        disabled={!templateWhatsappId}
                        fullWidth
                      />
                      <TextField
                        variant="outlined"
                        label="HEADER (opcional)"
                        value={wabaTemplateForm.headerText}
                        onChange={event =>
                          handleWabaTemplateFieldChange("headerText", event.target.value)
                        }
                        disabled={!templateWhatsappId}
                        fullWidth
                      />
                    </div>

                      <TextField
                      style={{ marginTop: 12 }}
                      variant="outlined"
                      label="BODY (obrigatório)"
                      value={wabaTemplateForm.bodyText}
                      onChange={event =>
                        handleWabaTemplateFieldChange("bodyText", event.target.value)
                      }
                      placeholder="Ex.: Ola {{1}}, seu atendimento esta pronto."
                      disabled={!templateWhatsappId}
                      multiline
                      rows={3}
                      fullWidth
                    />

                    {templateVariableIndexes(wabaTemplateForm.bodyText).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <Typography variant="body2" className={classes.introText}>
                          Exemplo de cada variável (obrigatório: a Meta avalia o template com estes valores)
                        </Typography>
                        {templateVariableIndexes(wabaTemplateForm.bodyText).map(index => (
                          <TextField
                            key={`template-example-${index}`}
                            style={{ marginTop: 8 }}
                            variant="outlined"
                            size="small"
                            label={`Exemplo para {{${index}}}`}
                            value={(wabaTemplateForm.bodyExamples || [])[index - 1] || ""}
                            onChange={event => {
                              const value = event.target.value;
                              setWabaTemplateForm(prev => {
                                const next = [...(prev.bodyExamples || [])];
                                next[index - 1] = value;
                                return { ...prev, bodyExamples: next };
                              });
                            }}
                            disabled={!templateWhatsappId}
                            fullWidth
                          />
                        ))}
                      </div>
                    )}

                    <TextField
                      style={{ marginTop: 12 }}
                      variant="outlined"
                      label="FOOTER (opcional)"
                      value={wabaTemplateForm.footerText}
                      onChange={event =>
                        handleWabaTemplateFieldChange("footerText", event.target.value)
                      }
                      disabled={!templateWhatsappId}
                      fullWidth
                    />

                    <div className={classes.wabaButtonsEditor}>
                      <Typography variant="body2" className={classes.introText}>
                        Botões do template (opcional, máximo 3)
                      </Typography>
                      {(wabaTemplateForm.buttons || []).map((button, index) => (
                        <div className={classes.wabaButtonRow} key={`waba-btn-${index}`}>
                          <TextField
                            select
                            variant="outlined"
                            label="Tipo"
                            value={button.type || "QUICK_REPLY"}
                            onChange={event =>
                              updateWabaTemplateButton(index, "type", event.target.value)
                            }
                            disabled={!templateWhatsappId}
                            fullWidth
                          >
                            <MenuItem value="QUICK_REPLY">QUICK_REPLY</MenuItem>
                            <MenuItem value="URL">URL</MenuItem>
                            <MenuItem value="PHONE_NUMBER">PHONE_NUMBER</MenuItem>
                          </TextField>
                          <TextField
                            variant="outlined"
                            label="Texto do botao"
                            value={button.text || ""}
                            onChange={event =>
                              updateWabaTemplateButton(index, "text", event.target.value)
                            }
                            disabled={!templateWhatsappId}
                            fullWidth
                          />
                          {String(button.type || "QUICK_REPLY") === "URL" ? (
                            <TextField
                              variant="outlined"
                              label="URL"
                              value={button.url || ""}
                              onChange={event =>
                                updateWabaTemplateButton(index, "url", event.target.value)
                              }
                              disabled={!templateWhatsappId}
                              fullWidth
                            />
                          ) : String(button.type || "QUICK_REPLY") === "PHONE_NUMBER" ? (
                            <TextField
                              variant="outlined"
                              label="Telefone"
                              value={button.phoneNumber || ""}
                              onChange={event =>
                                updateWabaTemplateButton(
                                  index,
                                  "phoneNumber",
                                  event.target.value
                                )
                              }
                              disabled={!templateWhatsappId}
                              fullWidth
                            />
                          ) : (
                            <TextField
                              variant="outlined"
                              label="Sem parametro extra"
                              value="-"
                              disabled
                              fullWidth
                            />
                          )}
                          <IconButton
                            onClick={() => removeWabaTemplateButton(index)}
                            disabled={!templateWhatsappId}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </div>
                      ))}
                      <div>
                        <Button
                          startIcon={<AddIcon />}
                          variant="outlined"
                          color="primary"
                          onClick={addWabaTemplateButton}
                          disabled={!templateWhatsappId}
                        >
                          Adicionar botão
                        </Button>
                      </div>
                    </div>

                    <div className={classes.wabaTemplateActions}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => loadWabaTemplates(templateWhatsappId)}
                        disabled={!templateWhatsappId || wabaTemplatesLoading}
                      >
                        {wabaTemplatesLoading ? "Atualizando..." : i18n.t("apiTools.updateList")}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={createWabaTemplate}
                        disabled={!templateWhatsappId || wabaTemplatesCreating}
                      >
                        {wabaTemplatesCreating ? "Enviando..." : i18n.t("apiTools.createTemplate")}
                      </Button>
                    </div>

                    <div className={classes.wabaTemplateList}>
                      {wabaTemplatesLoading ? (
                        <div className={classes.loadingRow}>
                          <CircularProgress size={16} />
                          <span>Carregando templates...</span>
                        </div>
                      ) : !wabaTemplates.length ? (
                        <div className={classes.emptyState}>
                          Nenhum template encontrado para esta conexão.
                        </div>
                      ) : (
                        <div className={classes.wabaTemplateCards}>
                          {wabaTemplates.map(item => (
                            <div className={classes.wabaTemplateRow} key={String(item.id)}>
                              <div className={classes.wabaTemplateHeader}>
                                <Typography variant="body2" style={{ fontWeight: 700 }}>
                                  {String(item.name || "-")} ({String(item.language || "-")})
                                </Typography>
                                <IconButton
                                  size="small"
                                  className={classes.dangerIconButton}
                                  onClick={() => deleteWabaTemplate(item)}
                                  title="Excluir template"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </div>
                              <div className={classes.wabaTemplateMeta}>
                                <Chip
                                  size="small"
                                  label={`Status: ${String(item.status || "-").toUpperCase()}`}
                                  className={getStatusChipClass(item.status)}
                                />
                                <Chip
                                  size="small"
                                  label={`Categoria: ${String(item.category || "-").toUpperCase()}`}
                                />
                                <Chip
                                  size="small"
                                  label={`Variáveis BODY: ${Number(item.bodyVariablesCount || 0)}`}
                                />
                                <Chip
                                  size="small"
                                  label={`Botões: ${Number(item.buttonsCount || 0)}`}
                                />
                              </div>
                              {item.bodyText ? (
                                <Typography variant="caption" className={classes.introText}>
                                  BODY: {String(item.bodyText)}
                                </Typography>
                              ) : null}
                              {item.rejectionReason ? (
                                <Typography variant="caption" style={{ color: "#b91c1c" }}>
                                  Motivo rejeição: {String(item.rejectionReason)}
                                </Typography>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={classes.wabaTemplateSection}>
                    <TextField
                      select
                      variant="outlined"
                      label={i18n.t("apiTools.whatsappConnection")}
                      value={wabaSelectedWhatsappId}
                      onChange={event => handleWabaSelectChange(event.target.value)}
                      fullWidth
                      disabled={!wabaConnections.length}
                    >
                      {wabaConnections.map(item => (
                        <MenuItem key={item.id} value={String(item.id)}>
                          {item.name || `Conexão #${item.id}`}
                        </MenuItem>
                      ))}
                    </TextField>

                    <div className={classes.wabaTemplateActions}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => loadWabaCalls(wabaSelectedWhatsappId)}
                        disabled={!wabaSelectedWhatsappId || wabaCallsLoading}
                      >
                        {wabaCallsLoading ? "Atualizando..." : i18n.t("apiTools.updateCalls")}
                      </Button>
                    </div>

                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      {i18n.t("apiTools.calls")} WABA
                    </Typography>
                    <Typography variant="body2" className={classes.introText}>
                      Eventos de chamada recebidos via webhook da Meta. Ao abrir o atendimento,
                      o agente atende no fluxo de ticket já existente.
                    </Typography>

                    <div className={classes.wabaTemplateList}>
                      {wabaCallsLoading ? (
                        <div className={classes.loadingRow}>
                          <CircularProgress size={16} />
                          <span>Carregando chamadas...</span>
                        </div>
                      ) : !wabaCalls.length ? (
                        <div className={classes.emptyState}>
                          Nenhum evento de chamada encontrado para esta conexão.
                        </div>
                      ) : (
                        <div className={classes.wabaTemplateCards}>
                          {wabaCalls.map(call => (
                            <div className={classes.callEventCard} key={String(call.id)}>
                              <div className={classes.callEventMeta}>
                                <Chip
                                  size="small"
                                  label={`Status: ${String(call.statusPt || "-")}`}
                                  className={getStatusChipClass(call.statusRaw)}
                                />
                                <Chip
                                  size="small"
                                  label={`Direção: ${String(call.directionPt || "-")}`}
                                />
                                <Chip
                                  size="small"
                                  label={`Tipo: ${String(call.typePt || "-")}`}
                                />
                              </div>

                              <Typography variant="body2" style={{ fontWeight: 700 }}>
                                {String(call.contactName || "Contato")}
                                {call.contactNumber
                                  ? ` (${String(call.contactNumber)})`
                                  : ""}
                              </Typography>

                              <div className={classes.callEventFooter}>
                                <Typography variant="caption" className={classes.introText}>
                                  Ticket #{String(call.ticketId)} - {formatCallDate(call.createdAt)}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() =>
                                    history.push(`/tickets/${String(call.ticketId)}`)
                                  }
                                >
                                  Abrir atendimento
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </Paper>
        )}
      </Paper>

      <Dialog
        open={Boolean(activeWindow)}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{dialogMeta.title}</DialogTitle>
        <DialogContent className={classes.dialogContent} dividers>
          <Typography variant="body2" className={classes.dialogDescription}>
            {dialogMeta.description}
          </Typography>

          {loading ? (
            <div className={classes.loadingRow}>
              <CircularProgress size={18} />
              <span>Carregando dados...</span>
            </div>
          ) : null}

          {!loading && activeWindow === "http" ? (
            <>
              <Paper className={classes.actionFormBox} variant="outlined">
                <div className={classes.dialogGrid}>
                  <TextField
                    variant="outlined"
                    label="Nome da ação"
                    placeholder="Ex.: Desbloquear cliente"
                    value={actionForm.name}
                    onChange={event =>
                      handleActionFieldChange("name", event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    select
                    variant="outlined"
                    label="Metodo"
                    value={actionForm.method}
                    onChange={event =>
                      handleActionFieldChange("method", event.target.value)
                    }
                    fullWidth
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                  </TextField>
                  <TextField
                    variant="outlined"
                    label="Endpoint"
                    placeholder="https://api.seusistema.com/v1/desbloquear"
                    value={actionForm.endpoint}
                    onChange={event =>
                      handleActionFieldChange("endpoint", event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    variant="outlined"
                    label="Timeout (ms)"
                    type="number"
                    value={actionForm.timeoutMs}
                    onChange={event =>
                      handleActionFieldChange("timeoutMs", event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    variant="outlined"
                    label="Descrição"
                    value={actionForm.description}
                    onChange={event =>
                      handleActionFieldChange("description", event.target.value)
                    }
                    fullWidth
                  />
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="api-tools-users-label">
                      Usuários autorizados
                    </InputLabel>
                    <Select
                      labelId="api-tools-users-label"
                      multiple
                      value={actionForm.allowedUserIds}
                      onChange={event =>
                        handleActionFieldChange(
                          "allowedUserIds",
                          normalizeIdList(event.target.value)
                        )
                      }
                      input={<OutlinedInput label="Usuários autorizados" />}
                      renderValue={selected => {
                        const selectedIds = Array.isArray(selected) ? selected : [];
                        if (!selectedIds.length) {
                          return "Sem permissão definida";
                        }
                        return users
                          .filter(user => selectedIds.includes(user.id))
                          .map(user => user.name)
                          .join(", ");
                      }}
                    >
                      {users.map(user => (
                        <MenuItem key={user.id} value={user.id}>
                          <Checkbox
                            checked={actionForm.allowedUserIds.includes(user.id)}
                            color="primary"
                          />
                          <ListItemText
                            primary={user.name}
                            secondary={user.email}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    variant="outlined"
                    label="Headers JSON"
                    value={actionForm.headersJson}
                    onChange={event =>
                      handleActionFieldChange("headersJson", event.target.value)
                    }
                    multiline
                    rows={3}
                    fullWidth
                  />
                  <TextField
                    variant="outlined"
                    label="Body template"
                    value={actionForm.bodyTemplate}
                    onChange={event =>
                      handleActionFieldChange("bodyTemplate", event.target.value)
                    }
                    multiline
                    rows={3}
                    fullWidth
                  />
                  <TextField
                    select
                    variant="outlined"
                    label="Com resultado da requisição"
                    value={actionForm.resultMode}
                    onChange={event =>
                      handleActionFieldChange("resultMode", event.target.value)
                    }
                    fullWidth
                  >
                    <MenuItem value="display">Exibir</MenuItem>
                    <MenuItem value="send_conversation">Enviar na conversa</MenuItem>
                    <MenuItem value="open_redirect">Abrir / Redirecionar</MenuItem>
                  </TextField>
                  <TextField
                    variant="outlined"
                    label="Template de resultado (opcional)"
                    value={actionForm.resultTemplate}
                    onChange={event =>
                      handleActionFieldChange("resultTemplate", event.target.value)
                    }
                    multiline
                    rows={3}
                    fullWidth
                  />
                  <TextField
                    variant="outlined"
                    label="Variáveis mapeadas da resposta"
                    value={
                      actionFormMappingsTemplatePreview ||
                      "Nenhuma variável mapeada. Use o botao Testar para mapear."
                    }
                    multiline
                    rows={4}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                  <div className={classes.templateHintRow}>
                    <Tooltip
                      arrow
                      placement="top-start"
                      title={
                        <div>
                          <Typography variant="caption" style={{ display: "block" }}>
                            Request/contato: <code>{"{{contact.document}}"}</code>,{" "}
                            <code>{"{{contact.documentFormatted}}"}</code>,{" "}
                            <code>{"{{contact.documentDigits}}"}</code>,{" "}
                            <code>{"{{contact.personType}}"}</code>,{" "}
                            <code>{"{{contact.name}}"}</code>,{" "}
                            <code>{"{{ticket.id}}"}</code>,{" "}
                            <code>{"{{observacao}}"}</code> (quando a ação pede observação).
                          </Typography>
                          <Typography variant="caption" style={{ display: "block" }}>
                            Resposta JSON: <code>{"{{response.statusCode}}"}</code>,{" "}
                            <code>{"{{response.json.link}}"}</code>,{" "}
                            <code>{"{{response.json.boletoUrl}}"}</code>,{" "}
                            <code>{"{{response.body}}"}</code>.
                          </Typography>
                          <Typography variant="caption" style={{ display: "block" }}>
                            Variáveis mapeadas: <code>{"{{vars.boleto_url}}"}</code>,{" "}
                            <code>{"{{vars.cliente_nome}}"}</code>.
                          </Typography>
                          <Typography variant="caption" style={{ display: "block" }}>
                            Exemplo body template:{" "}
                            <code>
                              {"{\"cpfCnpj\":\"{{contact.document}}\",\"tipo\":\"{{contact.personType}}\",\"ticketId\":\"{{ticket.id}}\"}"}
                            </code>
                          </Typography>
                          <Typography variant="caption" style={{ display: "block" }}>
                            Exemplo mapeamento JSON:{" "}
                            <code>
                              {
                                "[{\"key\":\"boleto_url\",\"source\":\"response.json\",\"path\":\"data.boleto.url\"}]"
                              }
                            </code>
                          </Typography>
                          <Typography variant="caption" style={{ display: "block" }}>
                            Exemplo template resultado:{" "}
                            <code>
                              {"Consulta concluida ({{response.statusCode}})\nBoleto: {{vars.boleto_url}}"}
                            </code>
                          </Typography>
                        </div>
                      }
                    >
                      <IconButton size="small" className={classes.templateHintIcon}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="caption" color="textSecondary">
                      Passe o mouse no icone para ver body, mapeamento e template.
                    </Typography>
                  </div>
                </div>

                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(actionForm.isActive)}
                      onChange={event =>
                        handleActionFieldChange("isActive", event.target.checked)
                      }
                    />
                  }
                  label="Ação ativa"
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(actionForm.requiresDocument)}
                      onChange={event =>
                        handleActionFieldChange("requiresDocument", event.target.checked)
                      }
                    />
                  }
                  label="Requer CPF/CNPJ válido para executar"
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(actionForm.requiresNote)}
                      onChange={event =>
                        handleActionFieldChange("requiresNote", event.target.checked)
                      }
                    />
                  }
                  label="Pedir uma observação ao atendente antes de executar"
                />
                {actionForm.requiresNote ? (
                  <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Pergunta para o atendente"
                    placeholder="Observação"
                    value={actionForm.noteLabel || ""}
                    onChange={event => handleActionFieldChange("noteLabel", event.target.value)}
                    inputProps={{ maxLength: 120 }}
                    helperText={
                      <>
                        O texto vai para a ferramenta em <code>{"{{observacao}}"}</code> (pronto
                        para ir entre aspas num corpo JSON), <code>{"{{observacao_texto}}"}</code>{" "}
                        (como foi digitado) e <code>{"{{observacao_url}}"}</code> (para a URL).
                      </>
                    }
                  />
                ) : null}

                <div className={classes.helperBox}>
                  <Typography variant="body2">
                    Permissões são aplicadas por usuário individual. Use{" "}
                    <code>{"{{contact.documentDigits}}"}</code> (somente numeros) ou{" "}
                    <code>{"{{contact.documentFormatted}}"}</code> (com mascara), além de{" "}
                    <code>{"{{contact.personType}}"}</code> no endpoint/body.
                  </Typography>
                </div>

                <div className={classes.permissionActions}>
                  <Button
                    onClick={resetActionForm}
                    disabled={saving}
                    variant="outlined"
                  >
                    Limpar
                  </Button>
                  <Button
                    onClick={saveAction}
                    color="primary"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? i18n.t("apiTools.saving") : actionForm.id ? "Atualizar" : "Criar ação"}
                  </Button>
                </div>
              </Paper>

              <Typography variant="subtitle2" className={classes.sectionTitle}>
                Ações cadastradas
              </Typography>
              <Paper variant="outlined" className={classes.tableWrap}>
                {actions.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ação</TableCell>
                        <TableCell>Metodo</TableCell>
                        <TableCell>CPF/CNPJ</TableCell>
                        <TableCell>Resultado</TableCell>
                        <TableCell>Usuários autorizados</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {actions.map(action => (
                        <TableRow key={action.id}>
                          <TableCell>
                            <div className={classes.actionName}>{action.name}</div>
                          </TableCell>
                          <TableCell>{action.method}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={action.requiresDocument ? "secondary" : "default"}
                              label={action.requiresDocument ? "Obrigatório" : "Opcional"}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={formatResultModeLabel(action.resultMode)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className={classes.userChips}>
                              {(action.allowedUsers || []).length ? (
                                (action.allowedUsers || []).map(user => (
                                  <Chip
                                    key={`${action.id}-${user.id}`}
                                    size="small"
                                    label={user.name}
                                  />
                                ))
                              ) : (
                                <Chip size="small" label="Sem permissão" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => openTestDialog(action)}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditAction(action)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => deleteAction(action.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" className={classes.emptyState}>
                    Nenhuma ação cadastrada ainda.
                  </Typography>
                )}
              </Paper>
            </>
          ) : null}

          {!loading && activeWindow === "permissions" ? (
            <>
              {actions.length ? (
                <>
                  <TextField
                    select
                    variant="outlined"
                    label="Ação HTTP"
                    value={selectedPermissionActionId}
                    onChange={event =>
                      setSelectedPermissionActionId(event.target.value)
                    }
                    fullWidth
                  >
                    {actions.map(action => (
                      <MenuItem key={action.id} value={String(action.id)}>
                        {action.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <FormControl variant="outlined" fullWidth style={{ marginTop: 12 }}>
                    <InputLabel id="api-tools-permission-users-label">
                      Usuários com permissão
                    </InputLabel>
                    <Select
                      labelId="api-tools-permission-users-label"
                      multiple
                      value={permissionUserIds}
                      onChange={event =>
                        setPermissionUserIds(normalizeIdList(event.target.value))
                      }
                      input={<OutlinedInput label="Usuários com permissão" />}
                      renderValue={selected => {
                        const selectedIds = Array.isArray(selected) ? selected : [];
                        if (!selectedIds.length) {
                          return "Sem permissão";
                        }
                        return users
                          .filter(user => selectedIds.includes(user.id))
                          .map(user => user.name)
                          .join(", ");
                      }}
                    >
                      {users.map(user => (
                        <MenuItem key={user.id} value={user.id}>
                          <Checkbox
                            checked={permissionUserIds.includes(user.id)}
                            color="primary"
                          />
                          <ListItemText
                            primary={user.name}
                            secondary={`${user.email} (${user.profile})`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Divider style={{ marginTop: 16, marginBottom: 16 }} />

                  <Typography variant="body2" color="textSecondary">
                    {selectedPermissionAction
                      ? `Ação selecionada: ${selectedPermissionAction.name}`
                      : "Selecione uma ação para editar permissão."}
                  </Typography>

                  <div className={classes.permissionActions}>
                    <Button
                      onClick={() =>
                        setPermissionUserIds(
                          selectedPermissionAction?.allowedUserIds || []
                        )
                      }
                      disabled={saving}
                      variant="outlined"
                    >
                      Reverter
                    </Button>
                    <Button
                      onClick={savePermissions}
                      color="primary"
                      variant="contained"
                      disabled={saving}
                    >
                      {saving ? i18n.t("apiTools.saving") : "Salvar permissões"}
                    </Button>
                  </div>
                </>
              ) : (
                <Typography variant="body2" className={classes.emptyState}>
                  Cadastre uma ação HTTP antes de configurar permissões.
                </Typography>
              )}
            </>
          ) : null}

          {!loading && activeWindow === "audit" ? (
            <>
              <div className={classes.dialogGrid}>
                <TextField
                  variant="outlined"
                  label="Data de"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  variant="outlined"
                  label="Data até"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </div>
              <div className={classes.helperBox}>
                <Typography variant="body2">
                  Proximo passo: conectar esta janela com o histórico real das execuções.
                </Typography>
              </div>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={loadData} color="primary" disabled={loading || saving}>
            {i18n.t("apiTools.reload")}
          </Button>
          <Button onClick={closeDialog} color="primary">
            {i18n.t("apiTools.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(testingAction)}
        onClose={closeTestDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Testar ação: {testingAction?.name || "-"}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.dialogGrid}>
            <TextField
              label="Ticket ID (opcional)"
              variant="outlined"
              value={testForm?.ticketId || ""}
              onChange={event => {
                const nextValue = event.target.value;
                setTestForm(prev => ({
                  ...(prev && typeof prev === "object" ? prev : {}),
                  ticketId: nextValue
                }));
              }}
              fullWidth
            />
            <TextField
              label="Contato ID (opcional)"
              variant="outlined"
              value={testForm?.contactId || ""}
              onChange={event => {
                const nextValue = event.target.value;
                setTestForm(prev => ({
                  ...(prev && typeof prev === "object" ? prev : {}),
                  contactId: nextValue
                }));
              }}
              fullWidth
            />
          </div>
          <TextField
            style={{ marginTop: 12 }}
            label="Variáveis JSON"
            variant="outlined"
            value={testForm?.variablesJson || ""}
            onChange={event => {
              const nextValue = event.target.value;
              setTestForm(prev => ({
                ...(prev && typeof prev === "object" ? prev : {}),
                variablesJson: nextValue
              }));
            }}
            multiline
            rows={5}
            helperText={
              'Não use "{{contact.document}}" aqui. Use Contato ID ou valor literal, ex.: {"cpf_cnpj":"12345678901"}.'
            }
            fullWidth
          />
          <div className={classes.mappingSection}>
            <Typography variant="subtitle2">
              Coletor de variáveis da resposta
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Teste a ação, selecione os campos do JSON e salve o mapeamento para usar no template com {" "}
              <code>{"{{vars.nome_variavel}}"}</code>.
            </Typography>

            <div className={classes.mappingGrid} style={{ marginTop: 10 }}>
              <TextField
                variant="outlined"
                size="small"
                label="Nome da variável"
                value={mappingDraft.key}
                onChange={event => {
                  const nextValue = event.target.value;
                  handleMappingDraftFieldChange("key", nextValue);
                }}
                fullWidth
              />
              <TextField
                select
                variant="outlined"
                size="small"
                label="Origem"
                value={mappingDraft.source}
                onChange={event => {
                  const nextSource = normalizeMappingSource(event.target.value);
                  setMappingDraft(prev => ({
                    ...prev,
                    source: nextSource,
                    path: nextSource === "response.json" ? prev.path : ""
                  }));
                }}
                fullWidth
              >
                {RESPONSE_MAPPING_SOURCE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select={mappingDraft.source === "response.json"}
                variant="outlined"
                size="small"
                label="Caminho JSON"
                value={mappingDraft.path}
                onChange={event => {
                  const nextPath = event.target.value;
                  setMappingDraft(prev => ({
                    ...prev,
                    path: nextPath,
                    key: prev.key || pathToVariableKey(nextPath)
                  }));
                }}
                disabled={mappingDraft.source !== "response.json"}
                fullWidth
              >
                {mappingDraft.source === "response.json" ? (
                  testResponsePathOptions.length ? (
                    testResponsePathOptions.map((item, index) => (
                      <MenuItem key={`path-${item.path}-${index}`} value={item.path}>
                        {item.path} {item.preview ? `- ${item.preview}` : ""}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">Execute um teste para listar caminhos</MenuItem>
                  )
                ) : null}
              </TextField>
              <TextField
                variant="outlined"
                size="small"
                label="Valor padrão (opcional)"
                placeholder='Ex.: "", 0, true'
                value={mappingDraft.defaultValue}
                onChange={event => {
                  const nextValue = event.target.value;
                  handleMappingDraftFieldChange("defaultValue", nextValue);
                }}
                fullWidth
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={addMappingFromDraft}
                disabled={saving}
              >
                Adicionar
              </Button>
            </div>

            {testResponsePathOptions.length ? (
              <div className={classes.mappingQuickList}>
                {testResponsePathOptions.slice(0, 40).map((item, index) => (
                  <div
                    className={classes.mappingQuickRow}
                    key={`quick-map-${item.path}-${index}`}
                  >
                    <Typography variant="caption">
                      <code>{item.path}</code>
                      {item.preview ? ` - ${item.preview}` : ""}
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => addQuickMappingFromPath(item.path)}
                    >
                      Usar
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}

            {testMappings.length ? (
              <div className={classes.mappingList}>
                {testMappings.map(item => (
                  <div className={classes.mappingRow} key={`mapping-${item.key}`}>
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>
                        {item.key}
                      </Typography>
                      <Typography className={classes.mappingMeta}>
                        {item.source}
                        {item.path ? ` -> ${item.path}` : ""}
                      </Typography>
                    </div>
                    <Typography variant="caption" color="textSecondary">
                      {Object.prototype.hasOwnProperty.call(item, "defaultValue")
                        ? `Padrão: ${formatMappingPreviewValue(item.defaultValue)}`
                        : "Sem valor padrão"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeMappingItem(item.key)}
                      title="Remover variável"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            ) : (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 8 }}
              >
                Nenhuma variável mapeada ainda.
              </Typography>
            )}

            <TextField
              style={{ marginTop: 10 }}
              variant="outlined"
              label="Variáveis para usar no template"
              value={
                testMappingsTemplatePreview ||
                "Nenhuma variável mapeada ainda."
              }
              multiline
              rows={4}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </div>
          {testResult ? (
            <div className={classes.monoBox}>
              {JSON.stringify(testResult, null, 2)}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTestDialog} disabled={saving}>
            {i18n.t("apiTools.close")}
          </Button>
          <Button
            onClick={saveMappingsToAction}
            variant="outlined"
            color="primary"
            disabled={saving || !testingAction}
          >
            Salvar mapeamento
          </Button>
          <Button
            onClick={runActionTest}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? "Executando..." : "Testar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ApiTools;

